import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fetchProducts } from "../lib/api";

const ANGLES = [135,160,110,145,120];
const COLORS = ["#05103a","#240a3a","#1a1a1f","#0d2010","#1a0a10"];
const TAG_COLORS = { "NEW DROP":"#00AAFF", BESTSELLER:"#FFD7B5", LIMITED:"#C0A0FF", "DROP 01":"#00AAFF", SOON:"#888", NEW:"#00AAFF" };

export default function HeroCarousel() {
  const { reduceMotion } = useTheme();
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [slides, setSlides] = useState([]);
  const lenRef = useRef(1);

  useEffect(() => {
    fetchProducts().then((products) => {
      const real = (products||[]).filter((p)=>!p.coming_soon && p.name !== "Coming Soon");
      const mapped = real.map((p,i) => ({
        id: p.id, angle: ANGLES[i%ANGLES.length], c1: "#000", c2: COLORS[i%COLORS.length],
        label: p.name, sub: `Drop 01 · ${p.category.charAt(0).toUpperCase()+p.category.slice(1)}`,
        tag: p.tag||"NEW DROP", image: p.image||null,
      }));
      const result = mapped.length ? mapped : [{ id:"p1", angle:135, c1:"#000", c2:"#05103a", label:"Superior Anarchyx", sub:"Drop 01 · T-shirts", tag:"DROP 01", image:null }];
      lenRef.current = result.length;
      setSlides(result);
    }).catch(()=>{
      const fb = [{ id:"p1", angle:135, c1:"#000", c2:"#05103a", label:"Superior Anarchyx", sub:"Drop 01 · T-shirts", tag:"DROP 01", image:null }];
      lenRef.current = 1; setSlides(fb);
    });
  }, []);

  const prev = useCallback(()=>setIdx((i)=>(i-1+lenRef.current)%lenRef.current),[]);
  const next = useCallback(()=>setIdx((i)=>(i+1)%lenRef.current),[]);

  const touchX = useRef(null);
  const onTouchStart = useCallback((e)=>{ touchX.current = e.touches[0].clientX; setPaused(true); },[]);
  const onTouchEnd = useCallback((e)=>{
    if(touchX.current===null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if(Math.abs(dx) > 40){ dx < 0 ? next() : prev(); }
    touchX.current = null;
    setPaused(false);
  },[next,prev]);

  useEffect(()=>{
    if(reduceMotion||paused||slides.length===0) return;
    const t = window.setInterval(next,3500);
    return ()=>window.clearInterval(t);
  },[reduceMotion,paused,next,slides.length]);

  if(slides.length===0) return (
    <div className="absolute inset-0 overflow-hidden rounded-[22px]" style={{background:"linear-gradient(135deg,#000,#05103a)"}}>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-display font-semibold select-none" style={{color:"#fff",opacity:0.08,fontSize:"clamp(10rem,26vw,22rem)",letterSpacing:"-0.06em"}}>MT</span>
      </div>
    </div>
  );

  const si = idx%slides.length;
  const cur = slides[si];

  return (
    <div className="absolute inset-0 overflow-hidden rounded-[22px] group"
      onMouseEnter={()=>setPaused(true)} onMouseLeave={()=>setPaused(false)}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="hero-carousel">
      {slides.map((s,i)=>(
        <div key={i} className={`mt-hero-slide ${i===si?"is-active":""}`}
          style={{background:`linear-gradient(${s.angle}deg, ${s.c1} 0%, ${s.c2} 100%)`}}>
          <div className="absolute inset-0 flex items-center justify-center">
            {s.image
              ? <img src={s.image} alt={s.label} className="absolute inset-0 w-full h-full object-cover" style={{opacity:1}} onError={(e)=>{e.currentTarget.style.display="none";}}/>
              : <span className="font-display font-semibold select-none" style={{color:"#fff",opacity:0.08,fontSize:"clamp(10rem,26vw,22rem)",letterSpacing:"-0.06em"}}>MT</span>
            }
          </div>
          <div className="absolute inset-0" style={{boxShadow:"inset 0 0 200px rgba(0,0,0,0.45)"}}/>
        </div>
      ))}
      <button type="button" onClick={()=>navigate(`/product/${cur.id}`)}
        className="absolute inset-0 z-10 flex flex-col justify-end p-5 text-left w-full" aria-label={`View ${cur.label}`}>
        <span className="inline-block mb-3 font-mono text-[0.58rem] tracking-mt uppercase px-3 py-1 rounded-full"
          style={{color:TAG_COLORS[cur.tag]||"#00AAFF",background:`${TAG_COLORS[cur.tag]||"#00AAFF"}18`,border:`1px solid ${TAG_COLORS[cur.tag]||"#00AAFF"}35`}}>
          {cur.tag}
        </span>
        <div className="flex items-end justify-between">
          <div>
            <p className="font-display text-xl font-light text-white leading-tight">{cur.label}</p>
            <p className="font-mono text-[0.58rem] tracking-mt uppercase mt-1" style={{color:"rgba(255,255,255,0.5)"}}>{cur.sub}</p>
          </div>
          <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 font-mono text-[0.6rem] tracking-mt uppercase text-white border rounded-full px-3 py-1.5" style={{borderColor:"rgba(255,255,255,0.3)"}}>View →</span>
        </div>
      </button>
      <button type="button" onClick={(e)=>{e.stopPropagation();prev();}}
        className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full items-center justify-center hidden md:flex"
        style={{background:"rgba(0,0,0,0.45)",border:"1px solid rgba(255,255,255,0.2)",color:"#fff"}} aria-label="Previous">
        <ChevronLeft size={14}/>
      </button>
      <button type="button" onClick={(e)=>{e.stopPropagation();next();}}
        className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full items-center justify-center hidden md:flex"
        style={{background:"rgba(0,0,0,0.45)",border:"1px solid rgba(255,255,255,0.2)",color:"#fff"}} aria-label="Next">
        <ChevronRight size={14}/>
      </button>
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
        {slides.map((_,i)=>(
          <button key={i} type="button" onClick={(e)=>{e.stopPropagation();setIdx(i);}}
            className="rounded-full transition-all duration-300"
            style={{width:i===si?18:6,height:6,background:i===si?"#fff":"rgba(255,255,255,0.35)"}} aria-label={`Slide ${i+1}`}/>
        ))}
      </div>
    </div>
  );
}
